import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Capacitor } from '@capacitor/core';
import { LoginService } from 'src/app/features/login/services/login.service';
import { AuthUserService } from '../auth/auth-user.service';

declare function gtag(command: 'set', target: 'user_properties', params: Record<string, string>): void;
declare function gtag(command: 'config', targetId: string, params: { page_path: string }): void;

@Injectable({ providedIn: 'root' })
export class AppAnalyticsService {
  private readonly GA4_MEASUREMENT_ID = 'G-15V6G6PLVY';

  constructor(
    private authUserService: AuthUserService,
    private loginService: LoginService
  ) {
    this.loginService.onSchoolSelected.subscribe(() => this.syncUserProperties());
  }

  /** Public method called by router changes */
  async trackView(pagePath: string): Promise<void> {
    const role = this.resolveRole();
    const platform = this.resolvePlatform();
    const isAuthenticated = !!this.authUserService.getToken() && role !== 'unauthenticated';

    // 📱 NATIVE MOBILE PIPELINE (iOS / Android)
    if (Capacitor.isNativePlatform()) {
      // 🛑 Guard: Completely block native tracking if unauthenticated
      if (!isAuthenticated) {
        console.log('GA4 Native Blocked: Ignoring unauthenticated mobile view.');
        return;
      }

      // Track mobile views with full user property context
      await FirebaseAnalytics.setUserProperty({ name: 'role', value: role });
      await FirebaseAnalytics.setUserProperty({ name: 'app_platform', value: platform });
      await FirebaseAnalytics.setScreenName({
        screenName: pagePath,
        nameOverride: pagePath
      });
      console.log('Native Firebase Screen Shipped:', pagePath, platform);
      return;
    }

    // 💻 STANDARD WEB BROWSER PIPELINE (gtag)
    if (typeof gtag === 'function') {
      if (isAuthenticated) {
        // Only set user dimensions if explicitly logged in
        gtag('set', 'user_properties', { role, app_platform: platform });
        console.log('Web Browser Gtag Shipped with User Context:', pagePath);
      } else {
        console.log('Web Browser Gtag Shipped: Anonymous (Page Path Only):', pagePath);
      }
      
      // Always track the page view configuration
      gtag('config', this.GA4_MEASUREMENT_ID, { page_path: pagePath });
    }
  }

  /** Trigger properties sync during standalone auth events (like school selection) */
  syncUserProperties(): void {
    const role = this.resolveRole();
    const platform = this.resolvePlatform();
    const isAuthenticated = !!this.authUserService.getToken() && role !== 'unauthenticated';

    if (Capacitor.isNativePlatform()) {
      if (!isAuthenticated) return;
      
      void FirebaseAnalytics.setUserProperty({ name: 'role', value: role });
      void FirebaseAnalytics.setUserProperty({ name: 'app_platform', value: platform });
    } else if (typeof gtag === 'function' && isAuthenticated) {
      // Only attach properties to the web session if logged in
      gtag('set', 'user_properties', { role, app_platform: platform });
    }
  }

  private resolveRole(): string {
    const schoolRole = this.authUserService.getSchoolUserAuthority()?.role;
    return schoolRole ? String(schoolRole) : 'unauthenticated';
  }

  private resolvePlatform(): string {
    switch (Capacitor.getPlatform()) {
      case 'android':
        return 'android';
      case 'ios':
        return 'ios';
      case 'web':
      case 'mobileweb':
        return 'web';
      default:
        return 'unknown';
    }
  }
}